import {
  createContext, PropsWithChildren, useContext, useEffect, useMemo,
  useState, useRef,
} from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import {
  Control, useWatch, UseFormSetValue,
} from "react-hook-form";
import {
  getMasterBusinessTrxAllArea,
  getMasterBusinessTrxDepartmentByCompany,
  getMasterBusinessTrxCompanyByPool,
  getMasterBusinessTrxGlList,
  getMasterBusinessTrxPoolByArea,
  getMasterBusinessTrxSegmentByCompany,
  getMasterBusinessTrxTypeList,
} from "../../../services/businessTrxService/businessTrxService";
import {
  formatStringCompanies, formatStringIds, groupedCompanies, groupedPools, groupedSegments,
} from "./utils";
import { CodeType, IBusinessTrxFormValues, IFormBusinessTrxContext } from "./types";
import { IOptionItemAutoComplete } from "../../../typings/common";
import { ITrxGl, ITrxType } from "../../../services/businessTrxService/types";

const FormBusinessTrxContext = createContext<IFormBusinessTrxContext | undefined>(undefined);
export const FormBusinessTrxProvider = ({
  children,
  control,
  step,
  setValue,
  isEditMode,
}: PropsWithChildren<{
  control: Control<IBusinessTrxFormValues>;
  step: number;
  setValue: UseFormSetValue<IBusinessTrxFormValues>;
  isEditMode: boolean;
}>) => {
  const {
    0: glValue,
    1: selectedAreaIds,
    2: selectedPoolIds,
    3: selectedCompanyIds,
    4: selectedCompanyRelations,
    5: selectedSegmentIds,
    6: selectedDepartmentIds,
    7: detailTransactionValues,
  } = useWatch({
    control,
    name: ["detailTransaksi.gl", "selectedAreaIds", "selectedPoolIds", "selectedCompanyIds", "selectedCompanyRelations", "selectedSegmentIds", "selectedDepartmentIds", "detailTransaksi",
    ],
    defaultValue: {
      detailTransaksi: {
        gl: "",
        deskripsi: "",
        deskripsiSap: "",
        tipeTransaksi: "",
        sap_shkzg: "",
        trx_code: "",
        code_type: CodeType.Pool,
      },
      selectedAreaIds: [],
      selectedPoolIds: [],
      selectedCompanyIds: [],
      selectedCompanyRelations: [],
      selectedSegmentIds: [],
      selectedDepartmentIds: [],
    },
  });
  const [paramsGl, setParamsGl] = useState({ search: "", page_number: 1 });
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const setDebouncedSearchGl = (search: string) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      setParamsGl((prev) => ({ ...prev, search }));
    }, 300);
  };

  const { data: rawTrxType } = useQuery({
    queryKey: ["getMasterBusinessTrxTypeList"],
    queryFn: () => getMasterBusinessTrxTypeList(),
    refetchOnWindowFocus: false,
    enabled: step === 0,
  });

  const { data: rawTrxGl, hasNextPage: hasNextGl, fetchNextPage: fetchNextGl } = useInfiniteQuery({
    queryKey: ["getMasterBusinessTrxGlList", paramsGl],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await getMasterBusinessTrxGlList({ ...paramsGl, page_number: Number(pageParam) });
      return res;
    },
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => {
      const meta = lastPage.metadata;
      if (!meta) return undefined;
      return meta.page < meta.total_pages ? meta.page + 1 : undefined;
    },
  });

  const {
    data: rawArea,
    isFetching: isFetchingArea,
  } = useQuery({
    queryKey: ["getMasterBusinessTrxAreaList"],
    queryFn: () => getMasterBusinessTrxAllArea(),
    refetchOnWindowFocus: false,
    enabled: step === 1,
    keepPreviousData: false,
  });

  const {
    data: rawPools,
    isFetching: isFetchingPool,
  } = useQuery({
    queryKey: ["getMasterBusinessTrxPoolByArea", selectedAreaIds],
    queryFn: () => getMasterBusinessTrxPoolByArea([...new Set(selectedAreaIds)].join(",")),
    refetchOnWindowFocus: false,
    enabled: (step === 2 && selectedAreaIds.length > 0)
      || (isEditMode && selectedAreaIds.length > 0),
    keepPreviousData: false,
  });

  const {
    data: rawCompanies,
    isFetching: isFetchingCompany,
  } = useQuery({
    queryKey: ["getMasterBusinessTrxCompanyByPool", selectedPoolIds],
    queryFn: () => getMasterBusinessTrxCompanyByPool(formatStringIds(selectedPoolIds)),
    refetchOnWindowFocus: false,
    enabled: (step === 3 && selectedPoolIds.length > 0)
      || (isEditMode && selectedPoolIds.length > 0),
    keepPreviousData: false,
  });

  const {
    data: rawSegments,
    isFetching: isFetchingSegment,
  } = useQuery({
    queryKey: ["getMasterBusinessTrxSegmentByCompany", selectedPoolIds, selectedCompanyIds],
    queryFn: () => getMasterBusinessTrxSegmentByCompany(
      formatStringIds(selectedPoolIds),
      formatStringCompanies(selectedCompanyIds),
    ),
    refetchOnWindowFocus: false,
    enabled: (step === 4 && selectedCompanyIds.length > 0)
      || (isEditMode && selectedCompanyIds.length > 0),
    keepPreviousData: false,
  });

  const {
    data: rawDepartments,
    isFetching: isFetchingDepartment,
  } = useQuery({
    queryKey: ["getMasterBusinessTrxDepartmentByCompany", selectedCompanyIds],
    queryFn: () => {
      const uniqueCompanies = Array.from(new Set(selectedCompanyIds.map((id) => id.split(".")[1])));
      const companyPayload = uniqueCompanies.map((companyCode) => {
        const relation = selectedCompanyRelations
          .find((rel) => rel.includes(`.${companyCode}`))?.split(".")[1] || "";
        return { company_code: companyCode, company_relation: relation };
      });
      return getMasterBusinessTrxDepartmentByCompany(companyPayload);
    },
    refetchOnWindowFocus: false,
    enabled: (step === 5 && selectedCompanyIds.length > 0)
      || (isEditMode && selectedCompanyIds.length > 0),
    keepPreviousData: false,
  });

  const trxGlOptions: IOptionItemAutoComplete[] = useMemo(() => (!rawTrxGl ? [] : rawTrxGl.pages
    .flatMap((page) => page.lstsmplmsttrxcode.map(({ gnlldr, trx_desc: trxDesc }: ITrxGl) => ({
      label: gnlldr,
      value: `${gnlldr}•${trxDesc}`,
      labelOverridden: `${gnlldr} • ${trxDesc}`,
      checked: false,
    })))), [rawTrxGl]);

  const trxTypeOptions: IOptionItemAutoComplete[] = useMemo(() => (
    !rawTrxType ? [] : rawTrxType.lstmsttrxtype
      .map(({ trx_type: type, description }: ITrxType) => ({
        label: description,
        value: type,
        checked: false,
      }))), [rawTrxType]);

  const resArea = useMemo(() => (!rawArea ? [] : rawArea.list_mstbusiness_area), [rawArea]);
  const resPoolByArea = useMemo(
    () => {
      if (!rawPools) return [];
      const uniquePools = [
        ...new Map(rawPools.list_mstbusiness_pool.map((pool) => [pool.pool_id, pool])).values(),
      ];
      return uniquePools;
    },
    [rawPools],
  );
  const resCompanyByPool = useMemo(
    () => (
      !rawCompanies ? [] : rawCompanies.list_business_company),
    [rawCompanies],
  );
  const resSegmentByCompany = useMemo(
    () => (
      !rawSegments ? [] : rawSegments.list_business_company),
    [rawSegments],
  );
  const resDepartment = useMemo(
    () => {
      if (!rawDepartments) return [];
      return rawDepartments.list_company.flatMap((c) => c.list_department);
    },
    [rawDepartments],
  );

  const pools = useMemo(() => {
    if (step !== 2 || !resPoolByArea) return {};
    return groupedPools(resPoolByArea);
  }, [step, resPoolByArea]);

  const companies = useMemo(() => {
    if (step !== 3 || !resCompanyByPool) return {};
    return groupedCompanies(resCompanyByPool);
  }, [step, resCompanyByPool]);

  const segments = useMemo(() => {
    if (step < 4 || !resSegmentByCompany) return {};
    return groupedSegments(resSegmentByCompany);
  }, [step, resSegmentByCompany]);

  const allAreaIds = useMemo(() => resArea.map((a) => a.area_id), [resArea]);
  const allPoolIds = useMemo(() => resPoolByArea.map((p) => p.pool_id), [resPoolByArea]);
  const allCompanyIds = useMemo(
    () => resCompanyByPool.flatMap((p) => p.list_company.map((c) => `${p.pool_id}.${c.company_code}`)),
    [resCompanyByPool],
  );
  const allCompanyRelations = useMemo(
    () => resCompanyByPool.flatMap((p) => p.list_company.map((c) => `${p.pool_id}.${c.company_relation}`)),
    [resCompanyByPool],
  );
  const allSegmentIds = useMemo(
    () => {
      const segmentIds = new Set<string>();
      resSegmentByCompany.forEach((pool) => {
        pool.list_company.forEach((company) => {
          const companyId = `${pool.pool_id}.${company.company_code}`;
          if (selectedCompanyIds.includes(companyId) && company.segment_code) {
            segmentIds.add(`${companyId}.${company.segment_code}`);
          }
        });
      });
      return Array.from(segmentIds);
    },
    [resSegmentByCompany, selectedCompanyIds],
  );
  const allDepartmentIds = useMemo(() => {
    if (!selectedSegmentIds?.length || !rawDepartments?.list_company?.length) return [];
    const departmentIds = new Set<string>();
    selectedSegmentIds.forEach((segmentId) => {
      // segmentId format: pool_id.company_code.segment_code
      const [, companyCode] = segmentId.split(".");
      const companyDepartments = rawDepartments.list_company
        .find((c) => c.company_code === companyCode)?.list_department || [];
      companyDepartments.forEach((d) => departmentIds.add(`${segmentId}.${d.department}`));
    });
    return Array.from(departmentIds);
  }, [selectedSegmentIds, rawDepartments]);

  useEffect(() => {
    if (step === 2) setValue("pilihSemuaPool", allPoolIds.length > 0 && selectedPoolIds.length === allPoolIds.length);
  }, [selectedPoolIds, allPoolIds, step, setValue]);

  useEffect(() => {
    if (step === 3) setValue("pilihSemuaPerusahaan", allCompanyIds.length > 0 && selectedCompanyIds.length === allCompanyIds.length);
  }, [selectedCompanyIds, allCompanyIds, step, setValue]);

  useEffect(() => {
    if (step === 4) setValue("pilihSemuaSegmen", allSegmentIds.length > 0 && selectedSegmentIds.length === allSegmentIds.length);
  }, [selectedSegmentIds, allSegmentIds, step, setValue]);

  useEffect(() => {
    if (step === 5) setValue("pilihSemuaDepartemen", allDepartmentIds.length > 0 && selectedDepartmentIds.length === allDepartmentIds.length);
  }, [selectedDepartmentIds, allDepartmentIds, step, setValue]);

  useEffect(() => {
    if (!isFetchingPool && selectedPoolIds) {
      const newIds = selectedPoolIds.filter((id) => allPoolIds.includes(id));
      if (newIds.length !== selectedPoolIds.length) {
        setValue("selectedPoolIds", newIds);
      }
    }
  }, [allPoolIds, selectedPoolIds, setValue, isFetchingPool]);

  useEffect(() => {
    if (!isFetchingCompany && selectedCompanyIds) {
      const newIds = selectedCompanyIds.filter((id) => allCompanyIds.includes(id));
      if (newIds.length !== selectedCompanyIds.length) {
        setValue("selectedCompanyIds", newIds);
      }
    }
  }, [allCompanyIds, selectedCompanyIds, setValue, isFetchingCompany]);

  useEffect(() => {
    if (!isFetchingSegment && selectedSegmentIds) {
      const newIds = selectedSegmentIds.filter((id) => allSegmentIds.includes(id));
      if (newIds.length !== selectedSegmentIds.length) {
        setValue("selectedSegmentIds", newIds);
      }
    }
  }, [allSegmentIds, selectedSegmentIds, setValue, isFetchingSegment]);

  useEffect(() => {
    if (!isFetchingDepartment && selectedDepartmentIds) {
      const newIds = selectedDepartmentIds.filter((id) => allDepartmentIds.includes(id));
      if (newIds.length !== selectedDepartmentIds.length) {
        setValue("selectedDepartmentIds", newIds);
      }
    }
  }, [allDepartmentIds, selectedDepartmentIds, setValue, isFetchingDepartment]);

  useEffect(() => {
    if (step === 2) setValue("poolsForValidation", JSON.stringify(pools));
  }, [pools, step, setValue]);

  useEffect(() => {
    if (step === 5 && rawDepartments) {
      setValue("departmentsForValidation", JSON.stringify(rawDepartments));
    }
  }, [rawDepartments, step, setValue]);

  useEffect(() => {
    if (resPoolByArea.length > 0) {
      setValue("resPoolByAreaForValidation", JSON.stringify(resPoolByArea));
    }
  }, [resPoolByArea, setValue]);

  useEffect(() => {
    if (resCompanyByPool.length > 0) {
      setValue("resCompanyByPoolForValidation", JSON.stringify(resCompanyByPool));
    }
  }, [resCompanyByPool, setValue]);

  const value = useMemo(() => ({
    rawTrxType,
    rawTrxGl,
    hasNextGl,
    fetchNextGl,
    rawArea,
    isFetchingArea,
    rawPools,
    isFetchingPool,
    rawCompanies,
    isFetchingCompany,
    rawSegments,
    isFetchingSegment,
    rawDepartments,
    isFetchingDepartment,
    trxGlOptions,
    trxTypeOptions,
    glValue,
    selectedAreaIds,
    selectedPoolIds,
    selectedCompanyIds,
    selectedCompanyRelations,
    selectedSegmentIds,
    selectedDepartmentIds,
    paramsGl,
    setParamsGl,
    resArea,
    resPoolByArea,
    resCompanyByPool,
    resSegmentByCompany,
    resDepartment,
    pools,
    companies,
    segments,
    allAreaIds,
    allPoolIds,
    allCompanyIds,
    allCompanyRelations,
    allSegmentIds,
    allDepartmentIds,
    setDebouncedSearchGl,
    isEditMode,
    detailTransactionValues,
  }), [
    rawTrxType, rawTrxGl, hasNextGl, fetchNextGl, rawArea, isFetchingArea, rawPools, isFetchingPool,
    rawCompanies, isFetchingCompany, rawSegments, isFetchingSegment, rawDepartments,
    isFetchingDepartment, trxGlOptions, trxTypeOptions, glValue, selectedAreaIds, selectedPoolIds,
    selectedCompanyIds, selectedCompanyRelations, selectedSegmentIds, selectedDepartmentIds,
    paramsGl, setParamsGl, resArea, resPoolByArea, resCompanyByPool, resSegmentByCompany,
    resDepartment, pools, companies, segments, allAreaIds, allPoolIds, allCompanyIds,
    allCompanyRelations, allSegmentIds, allDepartmentIds, setDebouncedSearchGl, isEditMode,
    detailTransactionValues,
  ]);

  return (
    <FormBusinessTrxContext.Provider value={value}>
      {children}
    </FormBusinessTrxContext.Provider>
  );
};

export const useFormBusinessTrx = () => {
  const context = useContext(FormBusinessTrxContext);
  if (!context) {
    throw new Error("useFormBusinessTrx must be used within a FormBusinessTrxProvider");
  }
  return context;
};
